import { roundToN } from 'utilities';
import { AdjustProbabilityValuesParams, ChangeItem } from './types';

/**
 * Adjusts probability/weighting values to maintain a sum of 1.
 * The cases below come from a request Matt T. made via Slack,
 * which is documented in SCAL-6218.
 * @param params - Parameters for probability adjustments
 * @returns Array of cell changes
 */
const adjustProbabilityValues = (params: AdjustProbabilityValuesParams) => {
  const { 
    cells, 
    changedCell, 
    newValue, 
    probabilityCellAlias, 
    summaryCellAlias, 
    summarySpreadsheet 
  } = params;

  const probabilityCells = Object.values(cells).filter(
    currentCell => {
      const matchesAlias = probabilityCellAlias === ''
        // Get all cells for the allocation scenario columns
        // (they are already filtered out by the caller)
        ? true
        // Condition for the summary weighting/probability cells
        : currentCell?.alias === probabilityCellAlias;
      return matchesAlias && !currentCell.readOnly;
    }
  );

  const changedCellIndex = probabilityCells.findIndex(c => c === changedCell);
  const numericValue = Number(newValue);
  
  // Store changes for later replication to summary cells
  const cellChanges = [];

  // Case 1: Setting to 1 - set all others to 0
  if (numericValue === 1) {
    probabilityCells.forEach(probCell => {
      if (probCell.key !== changedCell.key) {
        cellChanges.push({ cell: probCell, value: 0 });
      }
    });
    
  // Case 2: Only 2 cells - adjust the other cell
  } else if (probabilityCells.length === 2) {
    const otherCell = probabilityCells.find(c => c !== changedCell);
    if (otherCell) {
      // Round to 6 decimal places to avoid floating point precision issues
      cellChanges.push({ cell: otherCell, value: roundToN(1 - numericValue, 6) });
    }
    
  // Case 3: 3+ cells - last cell takes the hit unless last cell was changed
  } else {
    // Get current total of all cells except the changed one
    const currentTotal = probabilityCells.reduce((sum, c) => {
      if (c.key === changedCell.key) {
        return sum;
      }
      const cellValue = Number(c.value as string) || 0;
      return sum + cellValue;
    }, 0);

    const totalWithNew = currentTotal + numericValue;
    const difference = totalWithNew - 1;

    if (difference !== 0) {
      if (changedCellIndex === probabilityCells.length - 1) {
        // If last cell was changed, first cell takes the hit
        const firstCell = probabilityCells[0];
        const firstCellValue = Number(firstCell.value as string) || 0;
        const newFirstValue = Math.max(0, firstCellValue - difference);
        // Round to 6 decimal places to avoid floating point precision issues
        cellChanges.push({ cell: firstCell, value: roundToN(newFirstValue, 6) });
      } else {
        // Otherwise last cell takes the hit
        const lastCell = probabilityCells[probabilityCells.length - 1];
        const lastCellValue = Number(lastCell.value as string) || 0;
        const newLastValue = Math.max(0, lastCellValue - difference);
        // Round to 6 decimal places to avoid floating point precision issues
        cellChanges.push({ cell: lastCell, value: roundToN(newLastValue, 6) });
      }
    }
  }

  // Add the original change
  cellChanges.push({ cell: changedCell, value: numericValue });

  // Apply all changes and find corresponding summary cells
  const allChanges: ChangeItem[] = [];
  
  cellChanges.forEach(changeItem => {
    // Add to the changes array
    allChanges.push(changeItem);

    // Find and update the corresponding summary cell if summary info is provided
    if (summaryCellAlias && summarySpreadsheet) {
      const summaryCell = Object.values(summarySpreadsheet).find(
        sCell =>
          sCell.alias === summaryCellAlias
          && sCell.columnLegend === changeItem.cell.columnLegend
      );
      
      if (summaryCell) {
        allChanges.push({ cell: summaryCell, value: changeItem.value });
      }
    }
  });

  return allChanges;
};

export default adjustProbabilityValues;
